/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	CurrencyPipe,
	DecimalPipe,
	PercentPipe
} from '@angular/common';
import {
	Inject,
	Injectable,
	LOCALE_ID
} from '@angular/core';
import {
	IEntitySearch
} from '@entity/interfaces/entity-search.interface';
import {
	CommonTableComponent
} from '@shared/components/common-table/common-table.component';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	ExcelConstants
} from '@shared/constants/excel.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	ICommonTableColumn
} from '@shared/interfaces/application-objects/common-table-column.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IObjectSearch
} from '@shared/interfaces/application-objects/object-search.interface';
import {
	saveAs
} from 'file-saver';
import {
	JSONPath
} from 'jsonpath-plus';
import {
	cloneDeep,
	set
} from 'lodash-es';
import * as XLSX from 'xlsx';

/* eslint-enable max-len */

/**
 * A singleton class representing a data export service.
 *
 * @export
 * @class DataExportService
 */
@Injectable({
	providedIn: 'root'
})
export class DataExportService
{
	/**
	 * Initializes a new instance of the DataExportService class.
	 *
	 * @param {string} locale
	 * The locale as set by the application.
	 * @memberof DataExportService
	 */
	public constructor(
		@Inject(LOCALE_ID) private readonly locale: string)
	{
		this.currencyPipe =
			new CurrencyPipe(this.locale);
		this.decimalPipe =
			new DecimalPipe(this.locale);
		this.percentPipe =
			new PercentPipe(this.locale);
	}

	/**
	 * The currency pipe.
	 *
	 * @type {CurrencyPipe}
	 * @memberof DataExportService
	 */
	private readonly currencyPipe: CurrencyPipe;

	/**
	 * The decimal pipe.
	 *
	 * @type {DecimalPipe}
	 * @memberof DataExportService
	 */
	private readonly decimalPipe: DecimalPipe;

	/**
	 * The percent pipe.
	 *
	 * @type {PercentPipe}
	 * @memberof DataExportService
	 */
	private readonly percentPipe: PercentPipe;

	/**
	 * Gets the default precision for currency values
	 * if no precision is specified.
	 *
	 * @type {number}
	 * @memberof DataExportService
	 */
	private readonly currencyDefaultPrecision: number = 2;

	/**
	 * Gets the default precision for decimal values
	 * if no precision is specified.
	 *
	 * @type {number}
	 * @memberof DataExportService
	 */
	private readonly decimalDefaultPrecision: number = 2;

	/**
	 * Gets the default precision for percent values
	 * if no precision is specified.
	 *
	 * @type {number}
	 * @memberof DataExportService
	 */
	private readonly percentDefaultPrecision: number = 0;

	/**
	 * Exports data as a comma separated values .csv file.
	 *
	 * @param {string} fileName
	 * The file name. IE: `${fileName}.csv`.
	 * @param {any[]} data
	 * The data to export.
	 * @memberof DataExportService
	 */
	public exportDataAsCommaSeparatedValues(
		fileName: string,
		data: any[]): void
	{
		const commaSeparatedValues: string =
			this.transformToCommaSeparatedValues(
				data);

		const byteOrderMark = '\uFEFF';
		const blob: Blob =
		 	new Blob(
		 		[
		 			byteOrderMark,
		 			commaSeparatedValues
		 		],
		 		{
		 			type: 'text/csv;charset=utf-8'
		 		});

		saveAs(
			blob,
			`${fileName}.csv`);
	}

	/**
	 * Exports data as an Excel Workboook .xslx file.
	 *
	 * @param {string} fileName
	 * The file name. IE: `${fileName}.xslx`.
	 * @param {any[]} data
	 * The data to export.
	 * @param {ICommonTableColumn[]} columns
	 * The column definitions that contains the information
	 * to format the data for Excel.
	 * @memberof DataExportService
	 */
	public exportDataAsExcel(
		fileName: string,
		data: any[],
		columns: ICommonTableColumn[]): void
	{
		const transformedData: any[] =
			this.transformDataForExcel(
				data,
				columns);

		const worksheet: XLSX.WorkSheet =
			XLSX.utils.json_to_sheet(
				transformedData);

		this.formatExcelWorksheetColumns(
			worksheet,
			columns);

		const workbook: XLSX.WorkBook =
			XLSX.utils.book_new();

		XLSX.utils.book_append_sheet(
			workbook,
			worksheet,
			'Exported Data');

		const excelBuffer: any =
			XLSX.write(
				workbook,
				{
					bookType: 'xlsx',
					type: 'array'
				});
		const blob: Blob =
			new Blob(
				[
					excelBuffer
				],
				{
					type: 'application/octet-stream'
				});

		saveAs(
			blob,
			`${fileName}.xlsx`);
	}

	/**
	 * Exports table data.
	 *
	 * @async
	 * @param {string} fileName
	 * The file name. IE: `${fileName}.${exportType}`.
	 * @param {string} exportType
	 * The export type.
	 * @param {any[]} data
	 * The data to export.
	 * @param {ICommonTableColumn[]} columns
	 * The columns selected for exports.
	 * @param {IDynamicComponentContext<CommonTableComponent, any>} pageContext
	 * The table page context.
	 * @memberof DataExportService
	 */
	public async exportTableData(
		fileName: string,
		exportType: string,
		data: any[],
		columns: ICommonTableColumn[],
		pageContext: IDynamicComponentContext<CommonTableComponent, any>):
		Promise<void>
	{
		const filteredColumns: ICommonTableColumn[] =
			this.filterExportableColumns(
				columns);

		const transformedData: any[] =
			this.transformTableData(
				data,
				filteredColumns,
				pageContext,
				exportType !== AppConstants.exportTypes.excel);

		switch (exportType)
		{
			case AppConstants.exportTypes.excel:
				this.exportDataAsExcel(
					fileName,
					transformedData,
					filteredColumns);
				break;
			case AppConstants.exportTypes.commaSeparatedValues:
			default:
				this.exportDataAsCommaSeparatedValues(
					fileName,
					transformedData);
				break;
		}
	}

	/**
	 * Exports all table data.
	 *
	 * @async
	 * @param {string} fileName The file name.
	 * The file name. IE: `${fileName}.${exportType}`.
	 * @param {string} exportType
	 * The export type.
	 * @param {
	 * 	(objectSearch?: IObjectSearch | IEntitySearch) => Promise<any>
	 * } dataPromise
	 * The data promise used to load all table data.
	 * @param {(IObjectSearch | IEntitySearch)} searchObject
	 * The search object.
	 * @param {ICommonTableColumn[]} columns
	 * The columns selected for exports.
	 * @param {IDynamicComponentContext<CommonTableComponent, any>} pageContext
	 * The table page context.
	 * @memberof DataExportService
	 */
	public async exportAllTableData(
		fileName: string,
		exportType: string,
		dataPromise:
			(objectSearch?: IObjectSearch | IEntitySearch)
				=> Promise<any>,
		searchObject: IObjectSearch | IEntitySearch,
		columns: ICommonTableColumn[],
		pageContext: IDynamicComponentContext<CommonTableComponent, any>):
		Promise<void>
	{
		let data: any[] =
			await dataPromise(
				searchObject);

		if (data.length < searchObject.limit)
		{
			await this.exportTableData(
				fileName,
				exportType,
				data,
				columns,
				pageContext);

			return;
		}

		const searchObjectClone: IObjectSearch | IEntitySearch =
			cloneDeep(searchObject);

		let offset: number =
			searchObjectClone.limit;
		let fullDataset: any[] = data;
		let fullDatasetGathered: boolean = false;

		while (fullDatasetGathered === false)
		{
			searchObjectClone.offset = offset;

			data =
				await dataPromise(
					searchObjectClone);

			fullDataset =
				fullDataset.concat(
					data);

			if (data.length < searchObjectClone.limit)
			{
				fullDatasetGathered = true;
			}

			offset += searchObjectClone.limit;
		}

		await this.exportTableData(
			fileName,
			exportType,
			fullDataset,
			columns,
			pageContext);
	}

	/**
	 * Transforms table data into an array of objects
	 * that contain the values to export.
	 *
	 * @param {any[]} data
	 * The data to transform.
	 * @param {ICommonTableColumn[]} columns
	 * The column definitions that contains the information
	 * to transform and format the data.
	 * @param {IDynamicComponentContext<CommonTableComponent, any>} pageContext
	 * The table page context.
	 * @param {boolean} formatData
	 * A boolean indicating whether to format column values prior to export
	 * or return the raw values.
	 * @returns {any[]}
	 * The transformed data.
	 * @memberof DataExportService
	 */
	private transformTableData(
		data: any[],
		columns: ICommonTableColumn[],
		pageContext: IDynamicComponentContext<CommonTableComponent, any>,
		formatData: boolean = true): any[]
	{
		return data.map(
			(item: any) =>
				columns.reduce(
					(accumulator: any,
						tableColumn: ICommonTableColumn) =>
					{
						set(
							accumulator,
							tableColumn.columnHeader,
							this.formatColumn(
								item,
								tableColumn,
								pageContext,
								formatData));

						return accumulator;
					},
					{}));
	}

	/**
	 * Filters out columns that are not exportable and sorts the remaining
	 * columns by their display order.
	 *
	 * @param {ICommonTableColumn[]} columns
	 * The columns to filter and sort.
	 * @returns {ICommonTableColumn[]}
	 * The filtered and sorted columns.
	 * @memberof DataExportService
	 */
	private filterExportableColumns(
		columns: ICommonTableColumn[]): ICommonTableColumn[]
	{
		return columns
			.filter(
				(column: ICommonTableColumn) =>
					column.dataFormatType !== AppConstants.dataFormatTypes.icon
						&& column.displayOrder !== AppConstants
							.reservedTableDisplayOrders.actionColumn)
			.sort(
				(columnOne: ICommonTableColumn,
					columnTwo: ICommonTableColumn) =>
					columnOne.displayOrder - columnTwo.displayOrder);
	}

	/**
	 * Formats a column value.
	 *
	 * @param {any} item
	 * The item to format.
	 * @param {ICommonTableColumn} tableColumn
	 * The table column to format.
	 * @param {IDynamicComponentContext<CommonTableComponent, any>} pageContext
	 * The table page context.
	 * @param {boolean} formatValue
	 * A boolean indicating whether to format the column value
	 * or return the raw value.
	 * @returns {any}
	 * The formatted column value.
	 * @memberof DataExportService
	 */
	private formatColumn(
		item: any,
		tableColumn: ICommonTableColumn,
		pageContext: IDynamicComponentContext<CommonTableComponent, any>,
		formatValue: boolean = true): any
	{
		const columnValue: any =
			JSONPath(
				{
					path: tableColumn.dataKey,
					json: item
				})[0];

		if (!AnyHelper.isNullOrWhitespace(tableColumn.dataFunction))
		{
			return StringHelper.transformToFunction(
				tableColumn.dataFunction,
				pageContext)(columnValue);
		}

		if (AnyHelper.isNullOrWhitespace(columnValue))
		{
			return AppConstants.empty;
		}

		if (formatValue === false)
		{
			return columnValue;
		}

		return this.getFormattedColumn(
			columnValue,
			tableColumn);
	}

	/**
	 * Gets a formatted column.
	 *
	 * @param {any} columnValue
	 * The column value to format.
	 * @param {ICommonTableColumn} tableColumn
	 * The table column to format.
	 * @returns {any}
	 * The formatted column output.
	 * @memberof DataExportService
	 */
	private getFormattedColumn(
		columnValue: any,
		tableColumn: ICommonTableColumn): any
	{
		if (AnyHelper.isNullOrWhitespace(tableColumn.dataFormatType))
		{
			return columnValue;
		}

		switch(tableColumn.dataFormatType)
		{
			case AppConstants.dataFormatTypes.date:
			case AppConstants.dataFormatTypes.shortDate:
				return StringHelper.format(
					columnValue,
					AppConstants.formatTypes.shortDate);

			case AppConstants.dataFormatTypes.dateTime:
			case AppConstants.dataFormatTypes.longDate:
				return StringHelper.format(
					columnValue,
					AppConstants.formatTypes.longDate);

			case  AppConstants.dataFormatTypes.currency:
				return this.currencyPipe
					.transform(
						columnValue,
						AppConstants.localeDefinitions.unitedStatesDollars,
						AppConstants.localeDefinitions.symbol,
						tableColumn.formattedRoundingPrecision);

			case  AppConstants.dataFormatTypes.decimal:
				return this.decimalPipe
					.transform(
						columnValue,
						tableColumn.formattedRoundingPrecision);

			case  AppConstants.dataFormatTypes.percent:
				return this.percentPipe
					.transform(
						columnValue,
						tableColumn.formattedRoundingPrecision);

			default:
				return columnValue;
		}
	}

	/**
	 * Transforms data to a collection of comma separated values ready for
	 * display in a .csv file.
	 *
	 * @param {any[]} data
	 * The data to transform.
	 * @returns {string}
	 * The comma separated values array.
	 * @memberof DataExportService
	 */
	private transformToCommaSeparatedValues(
		data: any[]): string
	{
		if (data.length === 0)
		{
			return AppConstants.empty;
		}

		const headers: string[] =
			Object.keys(data[0]);

		const csvRows: string[] =
			data.map(
				(row: any) =>
					headers.map(
						(header: string) =>
						{
							const cell: any =
								row[header];
							const stringData: boolean =
								typeof cell ===
									AppConstants.propertyTypes.string;

							return stringData === true
								? `"${cell.replace(/"/g, '""')}"`
								: cell;
						})
						.join(
							AppConstants.characters.comma));

		const csvString: string =
			[headers.join(
				AppConstants.characters.comma),
			...csvRows]
				.join(
					`${AppConstants.characters.carriageReturn}`
						+ `${AppConstants.characters.lineFeed}`);

		return csvString;
	}

	/**
	 * Transforms the source data into a format suitable for Excel export.
	 *
	 * @param {any[]} data
	 * The data to transform.
	 * @param {ICommonTableColumn[]} columns
	 * The column definitions that contains the information
	 * to format the data for Excel.
	 * @returns {any[]}
	 * The transformed data.
	 * @memberof DataExportService
	 */
	private transformDataForExcel(
		data: any[],
		columns: ICommonTableColumn[]): any[]
	{
		return data.map(
			(item: any) =>
			{
				const transformedItem: any =
					{
						...item
					};
				Object.keys(item)
					.forEach(
						(key: string) =>
						{
							const matchingColumn: ICommonTableColumn =
								columns.find(
									(column: ICommonTableColumn) =>
										column.columnHeader === key);
							if (matchingColumn)
							{
								transformedItem[key] =
									this.getRawValue(
										item[key],
										matchingColumn);
							}
						});

				return transformedItem;
			});
	}

	/**
	 * Gets the raw value for a column based on its data format type.
	 *
	 * @param {any} columnValue
	 * The column value to get the raw value for.
	 * @param {ICommonTableColumn} tableColumn
	 * The column definition that contains the information to determine
	 * how to get the raw value.
	 * @returns {any}
	 * The raw column value.
	 * @memberof DataExportService
	 */
	private getRawValue(
		columnValue: any,
		tableColumn: ICommonTableColumn): any
	{
		if (AnyHelper.isNullOrWhitespace(
			tableColumn.dataFormatType))
		{
			return columnValue;
		}

		switch(tableColumn.dataFormatType)
		{
			case AppConstants.dataFormatTypes.date:
			case AppConstants.dataFormatTypes.shortDate:
			case AppConstants.dataFormatTypes.dateTime:
			case AppConstants.dataFormatTypes.longDate:
				return typeof columnValue === AppConstants.propertyTypes.string
					? new Date(columnValue)
					: columnValue;
			default:
				return columnValue;
		}
	}

	/**
	 * Formats the columns of an Excel worksheet
	 * based on the column definitions.
	 *
	 * @param {XLSX.WorkSheet} worksheet
	 * The worksheet to format.
	 * @param {ICommonTableColumn[]} columns
	 * The column definitions that contains the information
	 * to format the columns.
	 * @memberof DataExportService
	 */
	private formatExcelWorksheetColumns(
		worksheet: XLSX.WorkSheet,
		columns: ICommonTableColumn[]): void
	{
		columns.forEach(
			(column: ICommonTableColumn, index: number) => {
				switch (column.dataFormatType)
				{
					case AppConstants.dataFormatTypes.currency:
						this.formatExcelColumn(
							worksheet,
							index,
							this.getCurrencyFormat(
								column.roundingPrecision
									?? this.currencyDefaultPrecision));
						break;
					case AppConstants.dataFormatTypes.decimal:
						this.formatExcelColumn(
							worksheet,
							index,
							this.getNumberFormat(
								column.roundingPrecision
									?? this.decimalDefaultPrecision));
						break;
					case AppConstants.dataFormatTypes.percent:
						this.formatExcelColumn(
							worksheet,
							index,
							this.getPercentFormat(
								column.roundingPrecision
									?? this.percentDefaultPrecision));
						break;
					case AppConstants.dataFormatTypes.date:
					case AppConstants.dataFormatTypes.shortDate:
						this.formatExcelColumn(
							worksheet,
							index,
							ExcelConstants.formatTypes.shortDate);
						break;
					case AppConstants.dataFormatTypes.dateTime:
					case AppConstants.dataFormatTypes.longDate:
						this.formatExcelColumn(
							worksheet,
							index,
							ExcelConstants.formatTypes.longDate);
						break;
				}
			});
	}

	/**
	 * Applies a cell format to a specific column in an Excel worksheet.
	 *
	 * @param {XLSX.WorkSheet} worksheet
	 * The worksheet to format.
	 * @param {number} column
	 * The index of the column to format.
	 * @param {string} cellFormat
	 * The cell format to apply.
	 * @memberof DataExportService
	 */
	private formatExcelColumn(
		worksheet: XLSX.WorkSheet,
		column: number,
		cellFormat: string): void
	{
		const range = XLSX.utils.decode_range(
			worksheet[ExcelConstants.worksheetProperties.range]);

		// range.s.r + 1 skips the header row
		for (let row = range.s.r + 1; row <= range.e.r; row++)
		{
			const cellReference = XLSX.utils.encode_cell(
				{
					r: row,
					c: column
				});

			if (worksheet[cellReference]?.t === ExcelConstants.cellTypes.number)
			{
				worksheet[cellReference].z = cellFormat;
			}
		}
	}

	/**
	 * Gets the currency format string for a specific precision.
	 *
	 * @param {number} precision
	 * The number of decimal places.
	 * @returns {string}
	 * The currency format string.
	 * @memberof DataExportService
	 */
	private getCurrencyFormat(
		precision: number): string
	{
		const numberFormat: string =
			this.getNumberFormat(precision);

		return `${AppConstants.characters.dollarSign}${numberFormat}`;
	}

	/**
	 * Gets the percent format string for a specific precision.
	 *
	 * @param {number} precision
	 * The number of decimal places.
	 * @returns {string}
	 * The percent format string.
	 * @memberof DataExportService
	 */
	private getPercentFormat(
		precision: number): string
	{
		const numberFormat: string =
			this.getNumberFormat(precision);

		return `${numberFormat}${AppConstants.characters.percent}`;
	}

	/**
	 * Gets the number format string for a specific precision.
	 *
	 * @param {number} precision
	 * The number of decimal places.
	 * @returns {string}
	 * The number format string.
	 * @memberof DataExportService
	 */
	private getNumberFormat(
		precision: number): string
	{
		return precision
			? `${ExcelConstants.formatTypes.number}.${'0'.repeat(precision)}`
			: ExcelConstants.formatTypes.number;
	}
}